import { LOCALE_META, LOCALES } from '@ly360/i18n';
import { ChannelIcon, Dialog, ErrorState, Loading, PageHead, Select } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, Users as UsersIcon } from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from '../crm/forms';
import { api } from '../lib/api';

export interface TeamMember {
  userId: string;
  displayName: string;
  displayNameAr: string | null;
  locale: string;
  status: string;
  skills: string[];
  languages: string[];
}
export interface Team {
  id: string;
  name: string;
  nameAr: string | null;
  description: string | null;
  autoAssign: boolean;
  openCount: number;
  members: TeamMember[];
}
interface Rule {
  id: string;
  position: number;
  name: string;
  enabled: boolean;
  channel: string | null;
  channelAccountId: string | null;
  language: string | null;
  keywords: string[];
  teamId: string;
  skill: string | null;
}
interface UserRow {
  id: string;
  displayName: string;
  displayNameAr: string | null;
  locale: string;
  status: string;
  roles: string[];
}
interface AccountRow {
  id: string;
  channel: string;
  displayName: string;
}

const CHANNELS = ['whatsapp', 'email', 'webchat'] as const;

export const useTeams = () =>
  useQuery({ queryKey: ['teams'], queryFn: () => api<{ items: Team[] }>('/v1/teams'), staleTime: 30_000 });

export function useTeamName() {
  const { i18n } = useTranslation();
  const ar = i18n.language === 'ar' || i18n.language === 'ur';
  return (t: { name: string; nameAr: string | null } | null | undefined) =>
    t ? (ar && t.nameAr ? t.nameAr : t.name) : '';
}

const send = (path: string, method: string, body?: unknown) =>
  api<unknown>(path, { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });

function useSave<T>(fn: (v: T) => Promise<unknown>, onDone?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const k of ['teams', 'routing-rules', 'conversations'])
        void qc.invalidateQueries({ queryKey: [k] });
      onDone?.();
    },
  });
}

/** Administration → Teams: teams with members (languages, skills) and the ordered routing rules. */
export function TeamsPage() {
  const { t } = useTranslation();
  const teams = useTeams();
  const rules = useQuery({
    queryKey: ['routing-rules'],
    queryFn: () => api<{ items: Rule[] }>('/v1/routing-rules'),
  });
  const [editTeam, setEditTeam] = useState<Team | 'new' | null>(null);
  const [editMembers, setEditMembers] = useState<Team | null>(null);
  const [editRule, setEditRule] = useState<Rule | 'new' | null>(null);
  const [confirm, setConfirm] = useState<{ title: string; run: () => Promise<unknown> } | null>(null);

  return (
    <>
      <PageHead
        eyebrow={t('nav.admin')}
        title={t('teams.title')}
        subtitle={t('teams.subtitle')}
        actions={
          <button
            type="button"
            className="btn btn-grad"
            onClick={() => setEditTeam('new')}
            data-testid="new-team"
          >
            <Plus size={16} /> {t('teams.new')}
          </button>
        }
      />
      {teams.isLoading && <Loading />}
      {teams.isError && <ErrorState error={teams.error} onRetry={() => void teams.refetch()} />}
      {teams.data && !teams.data.items.length && (
        <section className="card empty-card">
          <UsersIcon size={22} className="faint" aria-hidden="true" />
          <p>{t('teams.empty')}</p>
        </section>
      )}
      <div className="team-grid" data-testid="team-list">
        {teams.data?.items.map((team) => (
          <TeamCard
            key={team.id}
            team={team}
            onEdit={() => setEditTeam(team)}
            onMembers={() => setEditMembers(team)}
            onDelete={() =>
              setConfirm({
                title: t('teams.deleteConfirm', { name: team.name }),
                run: () => send(`/v1/teams/${team.id}`, 'DELETE'),
              })
            }
          />
        ))}
      </div>

      <section className="card rules-card" style={{ marginTop: '1.25rem' }} data-testid="routing-rules">
        <div className="card-head">
          <div>
            <h2>{t('teams.rules.title')}</h2>
            <p className="faint" style={{ margin: 0, fontSize: 'var(--t-sm)' }}>
              {t('teams.rules.subtitle')}
            </p>
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => setEditRule('new')}
            disabled={!teams.data?.items.length}
            data-testid="new-rule"
          >
            <Plus size={16} /> {t('teams.rules.new')}
          </button>
        </div>
        {rules.isError && <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />}
        {rules.data && (
          <RuleList
            rules={rules.data.items}
            teams={teams.data?.items ?? []}
            onEdit={setEditRule}
            onDelete={(r) =>
              setConfirm({
                title: t('teams.rules.deleteConfirm', { name: r.name }),
                run: () => send(`/v1/routing-rules/${r.id}`, 'DELETE'),
              })
            }
          />
        )}
      </section>

      {editTeam && (
        <TeamDialog team={editTeam === 'new' ? null : editTeam} onClose={() => setEditTeam(null)} />
      )}
      {editMembers && <MembersDialog team={editMembers} onClose={() => setEditMembers(null)} />}
      {editRule && (
        <RuleDialog
          rule={editRule === 'new' ? null : editRule}
          teams={teams.data?.items ?? []}
          onClose={() => setEditRule(null)}
        />
      )}
      {confirm && <ConfirmDialog {...confirm} onClose={() => setConfirm(null)} />}
    </>
  );
}

function TeamCard({
  team,
  onEdit,
  onMembers,
  onDelete,
}: {
  team: Team;
  onEdit: () => void;
  onMembers: () => void;
  onDelete: () => void;
}) {
  const { t, i18n } = useTranslation();
  const name = useTeamName();
  const ar = i18n.language === 'ar' || i18n.language === 'ur';
  return (
    <article className="card team-card" data-testid="team-card" aria-label={name(team)}>
      <div className="card-head">
        <div style={{ minWidth: 0 }}>
          <h2 dir="auto">{name(team)}</h2>
          {team.description && (
            <p className="faint team-desc" dir="auto">
              {team.description}
            </p>
          )}
        </div>
        <div className="quick-actions">
          <button
            type="button"
            className="icon-btn"
            onClick={onEdit}
            aria-label={t('teams.edit', { name: name(team) })}
          >
            <Pencil size={15} />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={onDelete}
            aria-label={t('teams.delete', { name: name(team) })}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      <div className="team-meta">
        <span className={`badge ${team.autoAssign ? 'badge-success' : 'badge-neutral'}`}>
          {team.autoAssign ? t('teams.autoAssignOn') : t('teams.autoAssignOff')}
        </span>
        <span className="faint">{t('teams.openCount', { count: team.openCount })}</span>
      </div>
      <ul className="list-plain team-members">
        {team.members.map((m) => (
          <li key={m.userId}>
            <strong dir="auto">{ar && m.displayNameAr ? m.displayNameAr : m.displayName}</strong>
            <span className="chips">
              {(m.languages.length ? m.languages : [m.locale]).map((l) => (
                <span key={l} className="chip" lang={l}>
                  {LOCALE_META[l as keyof typeof LOCALE_META]?.nativeName ?? l}
                </span>
              ))}
              {m.skills.map((s) => (
                <span key={s} className="chip chip-skill">
                  <span className="val">{s}</span>
                </span>
              ))}
            </span>
          </li>
        ))}
        {!team.members.length && <li className="faint">{t('teams.noMembers')}</li>}
      </ul>
      <button type="button" className="btn btn-ghost" onClick={onMembers} data-testid="edit-members">
        <UsersIcon size={15} /> {t('teams.members')}
      </button>
    </article>
  );
}

function RuleList({
  rules,
  teams,
  onEdit,
  onDelete,
}: {
  rules: Rule[];
  teams: Team[];
  onEdit: (r: Rule) => void;
  onDelete: (r: Rule) => void;
}) {
  const { t, i18n } = useTranslation();
  const teamName = useTeamName();
  const reorder = useSave((ids: string[]) => send('/v1/routing-rules/order', 'PUT', { ids }));
  const toggle = useSave((r: Rule) => send(`/v1/routing-rules/${r.id}`, 'PATCH', { enabled: !r.enabled }));
  const move = (i: number, by: number) => {
    const ids = rules.map((r) => r.id);
    const [x] = ids.splice(i, 1);
    ids.splice(i + by, 0, x!);
    reorder.mutate(ids);
  };
  if (!rules.length) return <p className="faint">{t('teams.rules.empty')}</p>;
  return (
    <ol className="list-plain rule-list">
      {rules.map((r, i) => {
        const conditions: ReactNode[] = [
          r.channel && t(`channels.${r.channel}`),
          r.language && LOCALE_META[r.language as keyof typeof LOCALE_META]?.nativeName,
          r.keywords.length > 0 && (
            <>
              {t('teams.rules.mentions')}{' '}
              {r.keywords.map((k, n) => (
                <Fragment key={k}>
                  {n > 0 && ', '}
                  <bdi>{k}</bdi>
                </Fragment>
              ))}
            </>
          ),
          r.channelAccountId && t('teams.rules.oneAccount'),
        ].filter(Boolean);
        return (
          <li key={r.id} className={`rule-row${r.enabled ? '' : ' is-off'}`} data-testid="rule-row">
            <span className="rule-pos" aria-hidden="true">
              {i + 1}
            </span>
            <div className="rule-text">
              <strong dir="auto">{r.name}</strong>
              <span className="faint" dir="auto">
                {conditions.length
                  ? conditions.map((c, n) => (
                      <Fragment key={n}>
                        {n > 0 && ' · '}
                        {c}
                      </Fragment>
                    ))
                  : t('teams.rules.everything')}{' '}
                {i18n.dir() === 'rtl' ? '←' : '→'} {teamName(teams.find((x) => x.id === r.teamId))}
                {r.skill ? ` (${t('teams.rules.needsSkill', { skill: r.skill })})` : ''}
              </span>
            </div>
            <div className="quick-actions">
              <label className="switch" style={{ border: 0, padding: 0 }}>
                <span className="sr-only">{t('teams.rules.enabled', { name: r.name })}</span>
                <input type="checkbox" checked={r.enabled} onChange={() => toggle.mutate(r)} />
              </label>
              <button
                type="button"
                className="icon-btn"
                disabled={i === 0 || reorder.isPending}
                onClick={() => move(i, -1)}
                aria-label={t('teams.rules.moveUp', { name: r.name })}
              >
                <ArrowUp size={15} />
              </button>
              <button
                type="button"
                className="icon-btn"
                disabled={i === rules.length - 1 || reorder.isPending}
                onClick={() => move(i, 1)}
                aria-label={t('teams.rules.moveDown', { name: r.name })}
              >
                <ArrowDown size={15} />
              </button>
              <button
                type="button"
                className="icon-btn"
                onClick={() => onEdit(r)}
                aria-label={t('teams.rules.edit', { name: r.name })}
              >
                <Pencil size={15} />
              </button>
              <button
                type="button"
                className="icon-btn"
                onClick={() => onDelete(r)}
                aria-label={t('teams.rules.delete', { name: r.name })}
              >
                <Trash2 size={15} />
              </button>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Footer({ onClose, busy, label }: { onClose: () => void; busy: boolean; label: string }) {
  const { t } = useTranslation();
  return (
    <>
      <button type="button" className="btn btn-ghost" onClick={onClose}>
        {t('common.cancel')}
      </button>
      <button type="submit" className="btn btn-grad" disabled={busy}>
        {busy ? t('common.saving') : label}
      </button>
    </>
  );
}

function TeamDialog({ team, onClose }: { team: Team | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    name: team?.name ?? '',
    nameAr: team?.nameAr ?? '',
    description: team?.description ?? '',
    autoAssign: team?.autoAssign ?? true,
  });
  const save = useSave(
    () =>
      send(team ? `/v1/teams/${team.id}` : '/v1/teams', team ? 'PATCH' : 'POST', {
        name: f.name,
        nameAr: f.nameAr.trim() || null,
        description: f.description.trim() || null,
        autoAssign: f.autoAssign,
      }),
    onClose,
  );
  return (
    <Dialog
      open
      title={team ? t('teams.editTitle') : t('teams.new')}
      onClose={onClose}
      onSubmit={() => save.mutate(undefined)}
      testId="team-dialog"
      footer={
        <Footer
          onClose={onClose}
          busy={save.isPending}
          label={team ? t('common.save') : t('common.create')}
        />
      }
    >
      <div className="form-grid">
        <Field id="team-name" label={t('teams.field.name')}>
          <input
            id="team-name"
            className="input"
            required
            maxLength={120}
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </Field>
        <Field id="team-name-ar" label={t('teams.field.nameAr')}>
          <input
            id="team-name-ar"
            className="input"
            dir="rtl"
            lang="ar"
            maxLength={120}
            value={f.nameAr}
            onChange={(e) => setF({ ...f, nameAr: e.target.value })}
          />
        </Field>
      </div>
      <Field id="team-desc" label={t('teams.field.description')}>
        <textarea
          id="team-desc"
          className="input"
          rows={2}
          maxLength={500}
          dir="auto"
          value={f.description}
          onChange={(e) => setF({ ...f, description: e.target.value })}
        />
      </Field>
      <label className="switch">
        <span>
          {t('teams.field.autoAssign')}
          <span className="hint" style={{ display: 'block' }}>
            {t('teams.field.autoAssignHint')}
          </span>
        </span>
        <input
          type="checkbox"
          checked={f.autoAssign}
          onChange={(e) => setF({ ...f, autoAssign: e.target.checked })}
        />
      </label>
      {save.isError && <ErrorState error={save.error} />}
    </Dialog>
  );
}

interface MemberDraft {
  on: boolean;
  languages: string[];
  skills: string;
}

function MembersDialog({ team, onClose }: { team: Team; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const name = useTeamName();
  const ar = i18n.language === 'ar' || i18n.language === 'ur';
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<{ items: UserRow[] }>('/v1/users') });
  const [draft, setDraft] = useState<Record<string, MemberDraft>>(() =>
    Object.fromEntries(
      team.members.map((m) => [m.userId, { on: true, languages: m.languages, skills: m.skills.join(', ') }]),
    ),
  );
  const row = (id: string): MemberDraft => draft[id] ?? { on: false, languages: [], skills: '' };
  const update = (id: string, patch: Partial<MemberDraft>) =>
    setDraft({ ...draft, [id]: { ...row(id), ...patch } });
  const save = useSave(
    () =>
      send(`/v1/teams/${team.id}/members`, 'PUT', {
        members: Object.entries(draft)
          .filter(([, d]) => d.on)
          .map(([userId, d]) => ({
            userId,
            languages: d.languages,
            skills: d.skills
              .split(/[,،]/)
              .map((s) => s.trim())
              .filter(Boolean),
          })),
      }),
    onClose,
  );
  const staff = users.data?.items.filter((u) => u.status === 'active') ?? [];
  return (
    <Dialog
      open
      wide
      title={t('teams.membersTitle', { name: name(team) })}
      onClose={onClose}
      onSubmit={() => save.mutate(undefined)}
      testId="members-dialog"
      footer={<Footer onClose={onClose} busy={save.isPending} label={t('common.save')} />}
    >
      <p className="faint" style={{ marginTop: 0, fontSize: 'var(--t-sm)' }}>
        {t('teams.membersHint')}
      </p>
      {users.isLoading && <Loading />}
      {users.isError && <ErrorState error={users.error} />}
      <ul className="list-plain member-edit">
        {staff.map((u) => {
          const d = row(u.id);
          const label = ar && u.displayNameAr ? u.displayNameAr : u.displayName;
          return (
            <li key={u.id} className={d.on ? 'is-on' : ''} data-testid="member-row">
              <label className="member-pick">
                <input
                  type="checkbox"
                  checked={d.on}
                  onChange={(e) => update(u.id, { on: e.target.checked })}
                />
                <strong dir="auto">{label}</strong>
              </label>
              {d.on && (
                <div className="member-opts">
                  <fieldset className="chip-set">
                    <legend>{t('teams.field.languages')}</legend>
                    {LOCALES.map((l) => (
                      <label key={l} className="chip chip-toggle" lang={l}>
                        <input
                          type="checkbox"
                          checked={d.languages.includes(l)}
                          onChange={(e) =>
                            update(u.id, {
                              languages: e.target.checked
                                ? [...d.languages, l]
                                : d.languages.filter((x) => x !== l),
                            })
                          }
                        />
                        {LOCALE_META[l].nativeName}
                      </label>
                    ))}
                  </fieldset>
                  <Field
                    id={`skills-${u.id}`}
                    label={t('teams.field.skills')}
                    hint={t('teams.field.skillsHint')}
                  >
                    <input
                      id={`skills-${u.id}`}
                      className="input"
                      dir="auto"
                      value={d.skills}
                      placeholder="vip, complaints"
                      onChange={(e) => update(u.id, { skills: e.target.value })}
                    />
                  </Field>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {save.isError && <ErrorState error={save.error} />}
    </Dialog>
  );
}

function RuleDialog({ rule, teams, onClose }: { rule: Rule | null; teams: Team[]; onClose: () => void }) {
  const { t } = useTranslation();
  const teamName = useTeamName();
  const accounts = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api<{ accounts: AccountRow[] }>('/v1/integrations'),
  });
  const [f, setF] = useState({
    name: rule?.name ?? '',
    channel: rule?.channel ?? '',
    channelAccountId: rule?.channelAccountId ?? '',
    language: rule?.language ?? '',
    keywords: rule?.keywords.join(', ') ?? '',
    teamId: rule?.teamId ?? teams[0]?.id ?? '',
    skill: rule?.skill ?? '',
    enabled: rule?.enabled ?? true,
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });
  const save = useSave(() => {
    const body = {
      name: f.name,
      channel: f.channel || null,
      channelAccountId: f.channelAccountId || null,
      language: f.language || null,
      keywords: f.keywords
        .split(/[,،\n]/)
        .map((k) => k.trim())
        .filter(Boolean),
      teamId: f.teamId,
      skill: f.skill.trim().toLowerCase().replace(/\s+/g, '-') || null,
      enabled: f.enabled,
    };
    return send(rule ? `/v1/routing-rules/${rule.id}` : '/v1/routing-rules', rule ? 'PATCH' : 'POST', body);
  }, onClose);
  return (
    <Dialog
      open
      title={rule ? t('teams.rules.editTitle') : t('teams.rules.new')}
      onClose={onClose}
      onSubmit={() => save.mutate(undefined)}
      testId="rule-dialog"
      footer={
        <Footer
          onClose={onClose}
          busy={save.isPending}
          label={rule ? t('common.save') : t('common.create')}
        />
      }
    >
      <Field id="rule-name" label={t('teams.rules.field.name')}>
        <input
          id="rule-name"
          className="input"
          required
          maxLength={120}
          dir="auto"
          value={f.name}
          onChange={set('name')}
        />
      </Field>
      <h3 className="section-title">{t('teams.rules.when')}</h3>
      <div className="form-grid">
        <Field id="rule-channel" label={t('teams.rules.field.channel')}>
          <Select
            id="rule-channel"
            value={f.channel}
            onChange={(v) => setF({ ...f, channel: v })}
            options={[
              { value: '', label: t('teams.rules.any') },
              ...CHANNELS.map((c) => ({
                value: c,
                label: t(`channels.${c}`),
                icon: <ChannelIcon channel={c} size={11} />,
              })),
            ]}
          />
        </Field>
        <Field id="rule-account" label={t('teams.rules.field.account')}>
          <Select
            id="rule-account"
            value={f.channelAccountId}
            onChange={(v) => setF({ ...f, channelAccountId: v })}
            options={[
              { value: '', label: t('teams.rules.any') },
              ...(accounts.data?.accounts ?? []).map((a) => ({
                value: a.id,
                label: a.displayName,
                icon: <ChannelIcon channel={a.channel} size={11} />,
              })),
            ]}
          />
        </Field>
        <Field id="rule-language" label={t('teams.rules.field.language')}>
          <Select
            id="rule-language"
            value={f.language}
            onChange={(v) => setF({ ...f, language: v })}
            options={[
              { value: '', label: t('teams.rules.any') },
              ...LOCALES.map((l) => ({ value: l, label: LOCALE_META[l].nativeName })),
            ]}
          />
        </Field>
      </div>
      <Field
        id="rule-keywords"
        label={t('teams.rules.field.keywords')}
        hint={t('teams.rules.field.keywordsHint')}
      >
        <input
          id="rule-keywords"
          className="input"
          dir="auto"
          value={f.keywords}
          placeholder="complaint, شكوى, refund"
          onChange={set('keywords')}
        />
      </Field>
      <h3 className="section-title">{t('teams.rules.then')}</h3>
      <div className="form-grid">
        <Field id="rule-team" label={t('teams.rules.field.team')}>
          <Select
            id="rule-team"
            required
            value={f.teamId}
            onChange={(v) => setF({ ...f, teamId: v })}
            options={teams.map((x) => ({ value: x.id, label: teamName(x) }))}
          />
        </Field>
        <Field id="rule-skill" label={t('teams.rules.field.skill')} hint={t('teams.rules.field.skillHint')}>
          <input
            id="rule-skill"
            className="input"
            dir="ltr"
            maxLength={40}
            value={f.skill}
            onChange={set('skill')}
          />
        </Field>
      </div>
      <label className="switch">
        <span>{t('teams.rules.field.enabled')}</span>
        <input
          type="checkbox"
          checked={f.enabled}
          onChange={(e) => setF({ ...f, enabled: e.target.checked })}
        />
      </label>
      {save.isError && <ErrorState error={save.error} />}
    </Dialog>
  );
}

function ConfirmDialog({
  title,
  run,
  onClose,
}: {
  title: string;
  run: () => Promise<unknown>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const go = useSave(run, onClose);
  return (
    <Dialog
      open
      title={title}
      onClose={onClose}
      onSubmit={() => go.mutate(undefined)}
      testId="confirm-dialog"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-danger" disabled={go.isPending}>
            {t('common.delete')}
          </button>
        </>
      }
    >
      <p style={{ margin: 0 }}>{t('teams.deleteHint')}</p>
      {go.isError && <ErrorState error={go.error} />}
    </Dialog>
  );
}
